import Box from '@mui/material/Box';
import type { DynamicToolUIPart, ToolUIPart } from 'ai';
import type { VisualizationConfig } from '../tool-visualizations/types';
import { CopilotBusinessSignals, CopilotDataTable, CopilotKpiRow, CopilotLineChart } from '../tool-visualizations';

export type ToolInvocationPart = ToolUIPart | DynamicToolUIPart;

function VisualizationSwitch({ config }: { config: VisualizationConfig }) {
  switch (config.type) {
    case 'table':
      return <CopilotDataTable title={config.title} columns={config.columns} rows={config.rows} link={config.link} />;
    case 'kpis':
      return <CopilotKpiRow title={config.title} items={config.items} link={config.link} compact={config.compact} />;
    case 'line-chart':
      return (
        <CopilotLineChart
          title={config.title}
          dataset={config.dataset}
          xAxisKey={config.xAxisKey}
          lines={config.lines}
          height={config.height}
        />
      );
    case 'business-signals':
      return <CopilotBusinessSignals title={config.title} items={config.items} />;
    default:
      return null;
  }
}

function isVisualizationArray(output: unknown): output is VisualizationConfig[] {
  if (!Array.isArray(output)) return false;
  if (output.length === 0) return false;
  const firstItem = output[0];
  const isObject = typeof firstItem === 'object' && firstItem !== null;
  return isObject && 'type' in firstItem;
}

export function isToolResultRenderable(part: ToolInvocationPart): boolean {
  if (part.state !== 'output-available' || !part.output) return false;
  return isVisualizationArray(part.output);
}

export function ToolResultRenderer({ part }: { part: ToolInvocationPart }) {
  const output = part.output as VisualizationConfig[];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {output.map((config, configIndex) => (
        <VisualizationSwitch key={`${config.type}-${configIndex}`} config={config} />
      ))}
    </Box>
  );
}
